import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DataGroup, Timeline as VisTimeline } from "vis-timeline/esnext";
import { useVisTimeline } from "@/contexts/playback-context";
import { useSelector } from "@legendapp/state/react";
import { DataSet } from "vis-data";
import { useDisplayBufferPreview } from "./hooks/use-display-buffer";
import { TimelineLoader } from "./timeline-loader";
import { ExtendedVisTimeline } from "@/shared/types";
import {
  ControlEvent,
  ControlEventArgs,
  emitter,
} from "@/services/event-emitter";

import "vis-timeline/styles/vis-timeline-graph2d.css";
import { toggleTrackVisibility } from "@/virtual-store/state";
import moment from "moment-timezone";
import { useUpdateEffect } from "react-use";
import { Id } from "vis-data/declarations/data-interface";
import FilterSection from "./filter-section";
import { useTimelineState } from "./hooks/useTimelineState";
import { useTimelineSizing } from "./hooks/useTimelineSizing";
import { useTimelineGroups } from "./hooks/useTimelineGroups";
import { useTimelineOptions } from "./hooks/useTimelineOptions";
import { TimelineGroupConstants } from "./constants/timeline";

interface VisTimelineClickEventProps {
  event: MouseEvent;
  item?: number | string;
  group?: number | string;
  what?: string;
  pageX: number;
  pageY: number;
}

interface Group {
  index: number;
  id: string;
}

export interface TimelineGroupType extends DataGroup {
  id: string;
  content: string;
  color?: string;
  isHidden?: boolean;
  isSaved?: boolean;
  canHide?: boolean;
  canSave?: boolean;
  parentGroup?: string;
  showNested?: boolean;
  nestedGroups?: string[];
}

export const TIME_MARKER_ID = "time-cursor";

export function Timeline() {
  const timelineRef = useVisTimeline();
  const timelineOuterContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timebarRef = useRef<HTMLDivElement | null>(null);
  const filterSectionRef = useRef<HTMLDivElement | null>(null);
  const [dataset] = useState<DataSet<any, any>>(() => new DataSet());
  const [groups] = useState<DataSet<any, any>>(() => new DataSet());
  const isZooming = useRef(false);
  const isDragging = useRef(false);
  const isFocusAnimating = useRef(false);
  const [showSavedOnly, setShowSavedOnly] = useState<boolean>(false);
  const showSavedOnlyRef = useRef(showSavedOnly);
  const selectedGroupsRef = useRef<Group[] | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  const { startObserver: startBufferObserver, refresh } =
    useDisplayBufferPreview({
      groups,
      dataset,
      showSavedOnlyRef,
      debug: false,
      onClear: (ids?: string[]) => {
        const allIds = ids || dataset.getIds();
        const trackIds = allIds.filter(
          (id) => !["START", "END"].includes(id.toString())
        );
        dataset.remove(trackIds);
        groups.remove(trackIds);
      },
    });

  const { timelinePlayback, mediator, updateZoomLevel } = useTimelineState({
    dataset,
    groups,
    startBufferObserver,
  });

  useTimelineSizing(timelineOuterContainerRef, timelineRef);

  const { updateTimelineGroups, toggleSavedItemsVisibility } =
    useTimelineGroups(dataset, groups);

  const handleHide = (item: TimelineGroupType) => {
    const isSelected = selectedGroupsRef.current?.find(
      (group) => group.id === item.id
    );
    const newHiddenState = !item.isHidden;

    if (isSelected && selectedGroupsRef.current) {
      const updatedGroups = selectedGroupsRef.current
        .map((selectedGroup) => {
          const group = groups.get(selectedGroup.id);

          if (group) {
            return {
              ...group,
              isHidden: newHiddenState,
            };
          }
          return null;
        })
        .filter((group): group is Group => group !== null);

      groups.update(updatedGroups);

      updatedGroups.forEach((group) => onTrackHide(group.id));
    } else {
      // If the toggled item is not selected, only update itself
      groups.update({
        ...item,
        isHidden: newHiddenState,
      });

      onTrackHide(item.id);
    }
  };

  const handleSave = (item: any) => {
    const isSelected = selectedGroupsRef.current?.find(
      (group) => group.id === item.id
    );
    const newSavedState = !item.isSaved;

    if (isSelected && selectedGroupsRef.current) {
      const updatedGroups = selectedGroupsRef.current
        .map((selectedGroup) => {
          const group = groups.get(selectedGroup.id);

          if (group) {
            return {
              ...group,
              isSaved: newSavedState,
              visible: showSavedOnlyRef.current ? newSavedState : true,
            };
          }
          return null;
        })
        .filter((group): group is Group => group !== null);

      groups.update(updatedGroups);
    } else {
      groups.update({
        ...item,
        isSaved: newSavedState,
        visible: showSavedOnlyRef.current ? newSavedState : true,
      });
    }
  };

  const options = useTimelineOptions({ handleHide, handleSave });

  useEffect(() => {
    showSavedOnlyRef.current = showSavedOnly;
  }, [showSavedOnly]);

  // Zoom in/out visible window relative to cursor/center
  const onZoomChange = ({ zoomLevel }: ControlEventArgs["ZOOM_CHANGED"]) => {
    const currentTime = timelinePlayback.currentTime.peek();

    if (!timelineRef.current) return;

    isZooming.current = true;
    const window = timelineRef.current?.getWindow();

    // Calculate the center point of the current window
    const currentWindowCenter = currentTime
      ? currentTime
      : (window.start.getTime() + window.end.getTime()) / 2;

    // Calculate the new window duration
    const newWindowDuration =
      (options.zoomMax as number) - (options.zoomMin as number);

    // Calculate the new start and end dates to keep the current time marker centered
    const newStartDate =
      currentWindowCenter - (newWindowDuration / 2) * (1 - zoomLevel);
    const newEndDate =
      currentWindowCenter + (newWindowDuration / 2) * (1 - zoomLevel);

    timelinePlayback.zoomLevel.set(zoomLevel);

    timelineRef.current.setWindow(
      new Date(newStartDate),
      new Date(newEndDate),
      {
        animation: !isFocusAnimating.current,
      },
      () => {
        isZooming.current = false;
      }
    );
  };

  const onVideoDisabled = () => {
    groups.remove(TimelineGroupConstants.VideoBuffer);
    dataset.remove(TimelineGroupConstants.VideoBuffer);
  };

  const onClick = async (props: VisTimelineClickEventProps) => {
    const { event, group: clickedGroupId } = props;
    const target = event.target as HTMLElement;
    const labelsetElement = document.querySelector(".vis-labelset");
    const foregroundElement = document.querySelector(".vis-foreground");
    const selectedClassName = "selected";
    const group = groups.get(clickedGroupId as Id);

    updateTimelineGroups(group, !group.showNested);

    if (typeof clickedGroupId !== "string") {
      return;
    }

    if (!labelsetElement || !foregroundElement) {
      console.log("Required elements not found");
      return;
    }

    let clickedElement: HTMLElement | null = null;
    let clickedGroupIndex = -1;

    // Determine if click was in labelset or foreground
    if (target.closest(".vis-labelset")) {
      if (
        target.closest(".group-item-action-buttons") ||
        !target.closest(".vis-inner")
      ) {
        return;
      }

      clickedElement = target.closest(".vis-labelset > *") as HTMLElement;
      if (clickedElement) {
        clickedGroupIndex = Array.from(labelsetElement.children).indexOf(
          clickedElement
        );
      }
    } else if (target.closest(".vis-foreground")) {
      clickedElement = target.closest(".vis-foreground > *") as HTMLElement;
      if (clickedElement) {
        clickedGroupIndex = Array.from(foregroundElement.children).indexOf(
          clickedElement
        );
      }
    }

    if (clickedGroupIndex === -1) {
      return;
    }

    const isMultiSelectKey = event.metaKey || event.ctrlKey;

    if (!isMultiSelectKey) {
      if (
        selectedGroupsRef.current?.find(
          ({ index }) => index === clickedGroupIndex
        )
      ) {
        // Reset by removing 'selected' class from all elements
        const labelElements = labelsetElement.children;
        const foregroundElements = foregroundElement.children;

        for (let i = 0; i < labelElements.length; i++) {
          (labelElements[i] as HTMLElement).classList.remove(selectedClassName);
          (foregroundElements[i] as HTMLElement).classList.remove(
            selectedClassName
          );
        }

        selectedGroupsRef.current = [];
      } else {
        // Reset first
        const labelElements = labelsetElement.children;
        const foregroundElements = foregroundElement.children;

        for (let i = 0; i < labelElements.length; i++) {
          (labelElements[i] as HTMLElement).classList.remove(selectedClassName);
          (foregroundElements[i] as HTMLElement).classList.remove(
            selectedClassName
          );
        }

        selectedGroupsRef.current = [
          { index: clickedGroupIndex, id: clickedGroupId },
        ];
        (
          labelsetElement.children[clickedGroupIndex] as HTMLElement
        ).classList.add(selectedClassName);
        (
          foregroundElement.children[clickedGroupIndex] as HTMLElement
        ).classList.add(selectedClassName);
      }
    } else {
      if (!selectedGroupsRef.current) {
        selectedGroupsRef.current = [];
      }

      if (
        selectedGroupsRef.current.find(
          ({ index }) => index === clickedGroupIndex
        )
      ) {
        selectedGroupsRef.current = selectedGroupsRef.current.filter(
          ({ index }) => index !== clickedGroupIndex
        );
        (
          labelsetElement.children[clickedGroupIndex] as HTMLElement
        ).classList.remove(selectedClassName);
        (
          foregroundElement.children[clickedGroupIndex] as HTMLElement
        ).classList.remove(selectedClassName);
      } else {
        selectedGroupsRef.current.push({
          index: clickedGroupIndex,
          id: clickedGroupId,
        });
        (
          labelsetElement.children[clickedGroupIndex] as HTMLElement
        ).classList.add(selectedClassName);
        (
          foregroundElement.children[clickedGroupIndex] as HTMLElement
        ).classList.add(selectedClassName);
      }
    }
  };

  const onTimeChange = async (event: any) => {
    mediator.seek(event.time, event.id);
  };

  const onTimeChanged = (event: any) => {
    mediator.seeked(event.time, event.id);
  };

  const onRangeChange = (event: any) => {
    if (event.byUser) {
      isDragging.current = true;
      updateZoomLevel();
    }
  };

  const onRangeChanged = (event: any) => {
    if (event.byUser) {
      isDragging.current = false;
    }
  };

  const hasClass = (element: any, classname: string) => {
    while (element) {
      if (element.classList && element.classList.contains(classname)) {
        return true;
      }
      element = element.parentElement;
    }
    return false;
  };

  const onDoubleClick = (e: any) => {
    if (hasClass(e.event.target, "vis-foreground")) {
      timelinePlayback.currentTime.set(e.time.getTime());
      mediator.seeked(e.time, TIME_MARKER_ID);
      return;
    }
  };

  const onTrackHide = (trackId: string) => {
    toggleTrackVisibility(trackId);
    refresh();
  };

  const onFilterSaved = () => {
    toggleSavedItemsVisibility(!showSavedOnly);
    setShowSavedOnly(!showSavedOnly);
  };

  const { timezone } = useSelector(() => timelinePlayback.dateRange.get());

  useUpdateEffect(() => {
    timelineRef.current?.setOptions({
      moment: (date: moment.MomentInput) => moment(date).tz(timezone),
    });
  }, [timezone]);

  useEffect(() => {
    if (!containerRef.current) return;

    const timeline = new VisTimeline(
      containerRef.current,
      dataset,
      groups,
      options
    );

    timeline.setOptions({
      moment: (date: moment.MomentInput) => moment(date).tz(timezone),
    });

    timelineRef.current = timeline as ExtendedVisTimeline;

    // @ts-ignore
    timeline.itemSet.groupHammer.off("tap");

    // @ts-ignore
    timeline.itemSet.groupHammer.on("tap", function (event) {
      const target = event.target;

      if (hasClass(target, "vis-inner")) {
        return;
      } else {
        // @ts-ignore
        timeline.itemSet._onGroupClick(event);
      }
    });

    timeline.on("click", onClick);
    timeline.on("timechange", onTimeChange);
    timeline.on("timechanged", onTimeChanged);
    timeline.on("rangechange", onRangeChange);
    timeline.on("rangechanged", onRangeChanged);
    timeline.on("doubleClick", onDoubleClick);

    emitter.on(ControlEvent.VideoDisabled, onVideoDisabled);
    emitter.on(ControlEvent.ZoomChanged, onZoomChange);

    return () => {
      timeline.off("click", onClick);
      timeline.off("timechange", onTimeChange);
      timeline.off("timechanged", onTimeChanged);
      timeline.off("rangechange", onRangeChange);
      timeline.off("rangechanged", onRangeChanged);
      timeline.off("doubleClick", onDoubleClick);

      emitter.off(ControlEvent.VideoDisabled, onVideoDisabled);
      emitter.off(ControlEvent.ZoomChanged, onZoomChange);
    };
  }, []);

  useEffect(() => {
    const targetSection = document.querySelector(
      ".vis-timeline.vis-bottom.vis-ltr"
    ) as HTMLElement | null;

    if (targetSection) {
      targetSection.style.position = "relative";
      setPortalContainer(targetSection);
    } else {
      console.warn("Target section not found");
    }
  }, []);

  return (
    <div
      className="h-full timeline-outer-container"
      ref={timelineOuterContainerRef}
    >
      <div id="timebar" ref={timebarRef} />
      <div ref={containerRef} className="timeline-container"></div>
      <TimelineLoader />

      {portalContainer &&
        createPortal(
          <FilterSection
            ref={filterSectionRef}
            showSavedOnly={showSavedOnly}
            hasShowSavedButton
            onFilterToggle={() => onFilterSaved()}
            getAllTracks={() => {
              const ids = groups.getIds();
              const trackIds = ids.filter(
                (id) =>
                  ![
                    TimelineGroupConstants.TimeRange,
                    TimelineGroupConstants.VideoBuffer,
                  ].includes(String(id) as TimelineGroupConstants)
              );

              return trackIds;
            }}
          />,
          portalContainer
        )}
    </div>
  );
}
